import {
  AvailableBalanceCard,
  CurrentMonthlyVolumesCard,
  FlaggedTransactionsCard,
  PendingApprovalsCard,
  SearchInput,
  StatsWrapper,
  Tabs,
  TabsContainer,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@valiant/ui-shared'
import { useGetGroupStatsQuery } from '../generated/graphql'
import { CompaniesTab } from './tabs/CompaniesTab'
import { UsersTab } from './tabs/UsersTab'
import { TransactionsTab } from './tabs/TransactionsTab'
import { VolumesTab } from './tabs/VolumesTab'
import { useNavigation } from '../contexts/NavigationContext'
import { useQueryParams } from '@valiant/ui-shared'
import { useEffect, useState } from 'react'
import { usePendingAddresses } from '../hooks/usePendingAddresses'

interface AdminModeContentProps {
  onOpenNotifications?: () => void
}

export function AdminModeContent({ onOpenNotifications }: AdminModeContentProps) {
  const queryParams = useQueryParams('companies')
  const [searchTerm, setSearchTerm] = useState('')

  useEffect(() => {
    setSearchTerm('')
  }, [queryParams.tab])

  // Use navigation context
  const { selectedGroupId } = useNavigation()

  // Fetch group stats when groupId is available
  const [{ data: statsData, fetching: statsFetching }] = useGetGroupStatsQuery({
    variables: { groupId: selectedGroupId || '' },
    pause: !selectedGroupId,
  })

  const { count: pendingAddressesCount } = usePendingAddresses(selectedGroupId)
  const pendingApprovalsCount =
    (statsData?.getGroupStats?.pendingSwaps ?? 0) + pendingAddressesCount
  return (
    <>
      {/* Stats Cards */}
      <StatsWrapper>
        <AvailableBalanceCard
          availableBalance={statsData?.getGroupStats?.availableBalance ?? '-'}
          loading={statsFetching}
          onClick={() => {
            queryParams.updateMultiple({
              tab: 'companies',
              status: undefined,
              hasFunds: 'true',
            })
          }}
        />

        <PendingApprovalsCard
          pendingApprovals={pendingApprovalsCount}
          onClick={() => onOpenNotifications?.()}
        />
        <FlaggedTransactionsCard
          flaggedTransactions={statsData?.getGroupStats?.flaggedTransactions ?? 0}
          onClick={() => {
            queryParams.updateMultiple({
              tab: 'transactions',
              isFlagged: 'true',
              status: undefined,
              type: undefined,
            })
          }}
        />
        <CurrentMonthlyVolumesCard
          currentMonthlyVolumes={statsData?.getGroupStats?.currentMonthlyVolumes ?? '-'}
          previousMonthlyVolumes={statsData?.getGroupStats?.previousMonthlyVolumes ?? '-'}
          onClick={() => {
            queryParams.updateMultiple({ tab: 'volumes', status: undefined })
          }}
        />
      </StatsWrapper>

      {/* Tabs */}
      <Tabs
        value={queryParams.tab || 'companies'}
        onValueChange={(value) => {
          // Clear transactions filter when switching to other tabs
          if (value !== 'transactions') {
            queryParams.updateMultiple({
              tab: value,
              status: undefined,
              type: undefined,
              isFlagged: undefined,
              hasFunds: value === 'companies' ? queryParams.hasFunds : undefined,
            })
          } else {
            queryParams.updateTab(value)
          }
        }}
        className="space-y-4"
      >
        <TabsContainer>
          <TabsList>
            <TabsTrigger value="companies">Companies</TabsTrigger>
            <TabsTrigger value="users">Users</TabsTrigger>
            <TabsTrigger value="transactions">Transactions</TabsTrigger>
            <TabsTrigger value="volumes">Volumes</TabsTrigger>
          </TabsList>

          <SearchInput
            placeholder="Search"
            className="w-[200px]"
            value={searchTerm}
            onChange={setSearchTerm}
          />
        </TabsContainer>

        <TabsContent value="companies" className="space-y-6">
          <CompaniesTab groupId={selectedGroupId} searchTerm={searchTerm} />
        </TabsContent>

        <TabsContent value="users">
          <UsersTab
            groupId={selectedGroupId}
            searchTerm={searchTerm}
            availableRoleFilters={['TRADER', 'GROUP_OWNER']}
            allowedRoles={['GROUP_OWNER', 'TRADER']}
            showUnassignUserFromGroup={true}
          />
        </TabsContent>

        <TabsContent value="transactions">
          <TransactionsTab groupId={selectedGroupId} searchTerm={searchTerm} />
        </TabsContent>

        <TabsContent value="volumes">
          <VolumesTab groupId={selectedGroupId} searchTerm={searchTerm} />
        </TabsContent>
      </Tabs>
    </>
  )
}
